/**
 * AdminProductsEnhanced - Enhanced Products list view with standardized table
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  CurrencyCell,
  StatusCell,
  BooleanCell,
  BulkActionToolbar,
  TruncatedCell,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  BooleanFilter,
  NumericRangeFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
  BulkAssignModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { unifiedAPI, manufacturersAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  Edit,
  Trash2,
  RefreshCw,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Image,
} from 'lucide-react';

// Product type options - codes must match database values
const PRODUCT_TYPES = [
  { value: 'SMF', label: 'Small Format' },
  { value: 'LGF', label: 'Large Format' },
  { value: 'PRO', label: 'Promotional/Swag' },
  { value: 'AWD', label: 'Awards' },
];

// Status config for active/inactive
const ACTIVE_STATUS_CONFIG = {
  true: { label: 'Active', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  false: { label: 'Inactive', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

const AdminProductsEnhanced = ({ onViewProduct, onEditProduct, onCreateProduct }) => {
  const { isAdmin } = useAuth();
  
  // Categories and manufacturers for filters
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Fetch manufacturers for filter
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const mfrsData = await manufacturersAPI.getManufacturers({ limit: 200 });
        setManufacturers((mfrsData.manufacturers || mfrsData.items || []).map(m => ({ 
          value: m.id, 
          label: m.name 
        })));
      } catch (err) {
        console.error('Failed to fetch manufacturers:', err);
      }
    };
    fetchFilters();
  }, []);
  
  // Fetch function for products
  const fetchProducts = useCallback(async (params) => {
    return await unifiedAPI.adminGetProducts(params);
  }, []);
  
  // Use admin table hook
  const {
    data: products,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchProducts,
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
    defaultPageSize: 25,
    filterKeys: ['product_type', 'category_id', 'manufacturer_id', 'is_active', 'price_min', 'price_max'],
    transformResponse: (data) => ({
      items: data.products || data.items || [],
      total: data.total || 0
    })
  });
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await unifiedAPI.adminBulkDeleteProducts({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} product(s)`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete products');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Handle bulk assign manufacturer
  const handleBulkAssign = async (manufacturerId) => {
    setBulkActionLoading(true);
    try {
      for (const id of selectedIds) {
        await unifiedAPI.adminUpdateProduct(id, { manufacturer_id: manufacturerId });
      }
      
      toast.success(`Updated ${selectedIds.length} product(s)`);
      setShowAssignModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update products');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Table columns
  const columns = [
    {
      key: 'image_url',
      header: '',
      width: '60px',
      render: (value) => (
        <div className="w-10 h-10 rounded bg-ivory-100 flex items-center justify-center overflow-hidden">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <Image className="w-5 h-5 text-ivory-300" />
          )}
        </div>
      )
    },
    {
      key: 'name',
      header: 'Product',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-ivory-800 truncate max-w-[250px]" title={value}>
            {value || '—'}
          </p>
          <p className="text-xs text-ivory-500 font-mono">
            {row.internal_sku || row.sku || '—'}
          </p>
        </div>
      )
    },
    {
      key: 'product_type',
      header: 'Type',
      sortable: true,
      render: (value) => {
        const type = PRODUCT_TYPES.find(t => t.value === value);
        return (
          <span className="text-sm text-ivory-600 capitalize">
            {type?.label || value?.replace(/_/g, ' ') || '—'}
          </span>
        );
      }
    },
    {
      key: 'manufacturer_name',
      header: 'Manufacturer',
      sortable: false,
      render: (value) => (
        <TruncatedCell value={value} maxWidth={150} />
      )
    },
    {
      key: 'retail_price',
      header: 'Price',
      sortable: true,
      align: 'right',
      render: (value) => <CurrencyCell value={value} />
    },
    {
      key: 'cost_price',
      header: 'Cost',
      sortable: true,
      align: 'right',
      render: (value) => value !== undefined ? <CurrencyCell value={value} /> : <span className="text-ivory-400">—</span>
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: false,
      render: (value) => (
        <StatusCell 
          value={value?.toString()} 
          statusConfig={ACTIVE_STATUS_CONFIG} 
        />
      )
    },
    {
      key: 'taxable',
      header: 'Taxable',
      sortable: false,
      render: (value) => (
        <BooleanCell 
          value={value !== false} 
          trueLabel="Yes"
          falseLabel="No"
        />
      )
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onViewProduct?.(row.id);
            }}
            title="View Details"
            data-testid={`view-product-${row.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEditProduct?.(row.id);
            }}
            title="Edit Product"
            data-testid={`edit-product-${row.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];
  
  const filterLabels = {
    product_type: 'Type',
    category_id: 'Category',
    manufacturer_id: 'Manufacturer',
    is_active: 'Status',
    price_min: 'Min Price',
    price_max: 'Max Price',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-products-enhanced">
      <AdminListHeader
        title="Products"
        subtitle={total > 0 ? `${total.toLocaleString()} total products` : undefined}
        createLabel="Add Product"
        onCreate={onCreateProduct}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, SKU..."
          className="flex-1 min-w-[200px]"
        />
        
        <StatusFilter
          value={filters.product_type}
          onChange={(v) => setFilter('product_type', v)}
          options={PRODUCT_TYPES}
          placeholder="All Types"
          className="w-[150px]"
        />
        
        <StatusFilter
          value={filters.manufacturer_id}
          onChange={(v) => setFilter('manufacturer_id', v)}
          options={manufacturers}
          placeholder="All Manufacturers"
          className="w-[180px]"
        />
        
        <BooleanFilter
          value={filters.is_active}
          onChange={(v) => setFilter('is_active', v)}
          label="Status"
          trueLabel="Active"
          falseLabel="Inactive"
          allLabel="All"
          className="w-[130px]"
        />
        
        <NumericRangeFilter
          minValue={filters.price_min}
          maxValue={filters.price_max}
          onMinChange={(v) => setFilter('price_min', v)}
          onMaxChange={(v) => setFilter('price_max', v)}
          label="Price"
          prefix="$"
          className="w-[180px]"
        />
      </FilterBar>
      
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
      />
      
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setAssignType('manufacturer');
            setShowAssignModal(true);
          }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Assign Manufacturer
        </Button>
        
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      <AdminDataTable
        tableId="products"
        data={products}
        columns={columns}
        loading={loading}
        emptyMessage="No products found"
        emptyIcon={Package}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        density={density}
        onDensityChange={setDensity}
        showColumnSelector={true}
        resizableColumns={true}
        onRowClick={(row) => onEditProduct?.(row.id)}
        data-testid="products-data-table"
      />
      
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="product"
        loading={bulkActionLoading}
        blockedItems={[]}
        warnings={[
          'Soft-deleted products can be restored.',
          'Products will be hidden from store.'
        ]}
        requireReason={false}
      />
      
      <BulkAssignModal
        open={showAssignModal}
        onOpenChange={setShowAssignModal}
        onConfirm={handleBulkAssign}
        count={selectedCount}
        itemType="product"
        assignType="manufacturer"
        options={manufacturers}
        loading={bulkActionLoading}
      />
    </div>
  );
};

export default AdminProductsEnhanced;
